import React, { useCallback, useMemo, useRef, useState } from 'react'
import { CircularProgress, IconButton, makeStyles, Menu, MenuItem } from '@material-ui/core'
import PropTypes from 'prop-types'
import { ICON_NAMES } from '../../../../../constants'
import Icon from '../../../../atoms/Icon'
import { useComments } from '../../../Comments/CommentContext'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { parseEntryTypePermissions } from '../../../WealthJourney/v3/permissions'
import { useCurrentUser } from '../../../../../api/users'
import { usePolicy } from '../../../../../hooks/usePolicy'
import UpdateEntryDialog from '../../../WealthJourney/v3/dialogs/UpdateEntryDialog'
import RemoveEntryDialog from '../../../WealthJourney/v3/dialogs/RemoveEntryDialog'
import ManageTasksDialog from '../../../WealthJourney/v3/dialogs/ManageTasksDialog'

const useStyles = makeStyles((theme) => ({
  entryActions: {},
  menuItem: {
    display: 'flex',
    flexDirection: 'row',
    gap: theme.layout.gap.medium,
    alignItems: 'center'
  }
}))

function EntryMenu ({ entry, size = '24px', onUpdate }) {
  const { clientId } = useAppContext()
  const { showAttachmentsOnly } = useComments()
  const classes = useStyles()
  const [anchorEl, setAnchorEl] = useState()

  const updateDialogRef = useRef(null)
  const removeDialogRef = useRef(null)
  const tasksDialogRef = useRef(null)

  const { data: identity, isLoading } = useCurrentUser()
  const canEditMilestones = usePolicy('wj_edit_milestones')

  // Parse permissions for this entry type
  const permissions = parseEntryTypePermissions(identity, entry?.entryType, canEditMilestones)
  const canEdit = useMemo(() => !!permissions?.edit && entry?.entryType?.attributes, [permissions, entry])
  const canAttach = useMemo(() => !!permissions?.attach ?? false, [permissions])
  const canManageTasks = useMemo(() => !!permissions?.tasks, [permissions])
  const canDoSomething = useMemo(() => canEdit || canAttach || canManageTasks, [canEdit, canAttach, canManageTasks])

  const open = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    setAnchorEl(e.target)
  }, [])

  const close = useCallback(() => setAnchorEl(null), [])

  const withClose = useCallback((fn) => () => {
    fn()
    close()
  }, [close])

  const handleUpdate = useCallback(() => {
    updateDialogRef.current?.open(entry)
  }, [entry])

  const handleRemove = useCallback(() => {
    removeDialogRef.current?.open(entry)
  }, [entry])

  const handleTasks = useCallback(() => {
    tasksDialogRef.current?.open(entry)
  }, [entry])

  const handleAttachments = useCallback(() => {
    showAttachmentsOnly({
      clientId,
      title: `Attachments for ${entry.entryText}`,
      levelTypeId: 1500,
      levelId: entry.entryId
    })
  }, [clientId, entry, showAttachmentsOnly])

  if (isLoading) {
    return (
      <div className={classes.entryActions}>
        <CircularProgress size={16} />
      </div>
    )
  }

  if (!canDoSomething) {
    return null
  }

  return (
    <>
      <div className={classes.entryActions}>
        <IconButton aria-controls='more-opts' aria-haspopup='true' onClick={open}>
          <Icon name={ICON_NAMES.vertDots} customSize={size} />
        </IconButton>
        <Menu
          id='entry-actions'
          keepMounted
          anchorEl={anchorEl}
          open={!!anchorEl}
          onClose={close}
        >
          {canEdit && (
            <MenuItem onClick={withClose(handleUpdate)}>
              <div className={classes.menuItem}>
                <Icon name='edit' />
                <div>Edit</div>
              </div>
            </MenuItem>
          )}
          {canAttach && (
            <MenuItem onClick={withClose(handleAttachments)}>
              <div className={classes.menuItem}>
                <Icon name='document' />
                <div>Attachments</div>
              </div>
            </MenuItem>
          )}
          {canManageTasks && (
            <MenuItem onClick={withClose(handleTasks)}>
              <div className={classes.menuItem}>
                <Icon name='check' />
                <div>Tasks</div>
              </div>
            </MenuItem>
          )}
          {canEdit && <hr />}
          {canEdit && (
            <MenuItem onClick={withClose(handleRemove)}>
              <div className={classes.menuItem}>
                <Icon name='delete' />
                <div>Archive</div>
              </div>
            </MenuItem>
          )}
        </Menu>
      </div>

      <UpdateEntryDialog ref={updateDialogRef} onComplete={onUpdate} />
      <RemoveEntryDialog ref={removeDialogRef} onComplete={onUpdate} />
      <ManageTasksDialog ref={tasksDialogRef} onComplete={onUpdate} />
    </>
  )
}

EntryMenu.propTypes = {
  entry: PropTypes.object.isRequired,
  size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onUpdate: PropTypes.func
}

export default EntryMenu
